/**
 * Which Dooglie channel this bundle is — decided by the Vite mode it was built
 * with, never by the page's hostname.
 *
 * - `production`: `npm run build` → https://app.dooglie.com ("Dooglie").
 * - `dev-hosted`: `npm run build:dev-hosted` → https://dev.dooglie.com
 *   ("Dooglie Dev"): the dev backend, the dev extension, the dev desktop app.
 * - `local`: the dev server on someone's localhost (and the test runner).
 *
 * Everything that used to reason "not localhost, so production" asks this
 * instead, because the hosted dev build is neither.
 */
export type Channel = 'production' | 'dev-hosted' | 'local';

export const CHANNEL: Channel =
  import.meta.env.MODE === 'production'
    ? 'production'
    : import.meta.env.MODE === 'dev-hosted'
      ? 'dev-hosted'
      : 'local';

export const IS_PRODUCTION_CHANNEL = CHANNEL === 'production';

/**
 * The Sentry environment for this bundle. The hosted dev build is
 * `development` — dev.dooglie.com is a shared, deployed dev stack, not somebody's
 * machine — so its errors stay out of `production` and apart from `local`.
 */
export const SENTRY_ENVIRONMENT: 'production' | 'development' | 'local' =
  CHANNEL === 'production'
    ? 'production'
    : CHANNEL === 'dev-hosted'
      ? 'development'
      : 'local';

/**
 * A `VITE_*` value the bundle cannot work without. Missing means the env file
 * for this mode is incomplete; say so at startup rather than run with a value
 * quietly borrowed from another channel. vite.config.ts checks the same names
 * at build time, so a build with a gap never gets as far as a browser.
 */
export function requireEnv(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set for Vite mode "${import.meta.env.MODE}" — add it to .env.${import.meta.env.MODE}`
    );
  }
  return value;
}
